import {
    FC,
    ReactElement,
    useMemo,
    useState,
} from 'react';

import { useDebounce } from 'react-use';

import { debounceDelay, longDebounceDelay } from '../../../constants';
import { SkillsCard } from '../../../containers';
import { userHasRole } from '../../../helpers/role';
import { transformOccupationToSearchableOption } from '../../../models/@competentNL/Occupations/OccupationsTransformers';
import { SkillBase, SkillsFormData } from '../../../models/@competentNL/Skills/Skills';
import { setOccupation } from '../../../redux/competentNL/competentNL';
import { fetchCompetentNLOccupation, fetchCompetentNLOccupations, fetchCompetentNLSkills } from '../../../redux/competentNL/competentNLActions';
import { updateCandidateSkills } from '../../../redux/skills/skillsActions';
import { useTypedDispatch, useTypedSelector } from '../../../redux/store';
import { SearchableOption, UserRole } from '../../../types';

interface ConnectedSkillsCardProps {
    className?: string;
}

const ConnectedSkillsCard: FC<ConnectedSkillsCardProps> = ({
    className = '',
}): ReactElement => {
    const dispatch = useTypedDispatch();

    const candidate = useTypedSelector(state => state.candidateReducer.candidate);
    const user = useTypedSelector(state => state.userReducer.user);

    const skillsPassportIsLoading = useTypedSelector(state => state.skillsPassportReducer.isLoading);
    const skillsPassportError = useTypedSelector(state => state.skillsPassportReducer.error);
    const skills = useTypedSelector(state => state.skillsPassportReducer.careerSkills);

    const occupations = useTypedSelector(state => state.competentNLReducer.occupations);
    const occupation = useTypedSelector(state => state.competentNLReducer.occupation);
    const skillsIsLoading = useTypedSelector(state => state.competentNLReducer.skillsIsLoading);
    const skillBases = useTypedSelector(state => state.competentNLReducer.skillBases);
    const skillsError = useTypedSelector(state => state.competentNLReducer.skillsError);

    const [skillQuery, setSkillQuery] = useState<string>('');
    const [occupationQuery, setOccupationQuery] = useState<string>('');

    const occupationOptions = useMemo((): SearchableOption[] => (
        occupations.map(transformOccupationToSearchableOption)
    ), [occupations]);

    const occupationSkillOptions = useMemo((): SkillBase[] => {
        if (!occupation) {
            return [];
        }

        const { essentialSkills, importantSkills, extraSkills } = occupation;

        return [...essentialSkills, ...importantSkills, ...extraSkills];
    }, [occupation]);

    useDebounce((): void => {
        if (skillQuery.length > 1) {
            dispatch(fetchCompetentNLSkills(skillQuery));
        }
    }, longDebounceDelay, [skillQuery]);

    useDebounce((): void => {
        if (occupationQuery) {
            dispatch(fetchCompetentNLOccupations(occupationQuery));
            return;
        }

        dispatch(setOccupation(undefined));
    }, debounceDelay, [occupationQuery]);

    const handleSelectOccupation = (occupationId: string): void => {
        dispatch(fetchCompetentNLOccupation(occupationId));
    };

    const handleUpdateSkills = (skillsFormData: SkillsFormData): void => {
        if (candidate?.uuid) {
            dispatch(updateCandidateSkills(skillsFormData, candidate.uuid));
        }
    };

    const handleCloseSkillsDialog = (): void => {
        dispatch(setOccupation(undefined));
    };

    return (
        <SkillsCard
            isLoading={skillsPassportIsLoading}
            suggestionIsLoading={skillsIsLoading}
            isEditable={!skillsPassportError && userHasRole(UserRole.candidate, user?.roles)}
            hasError={!!skillsPassportError}
            userRoles={user?.roles}
            activeSkills={skills}
            searchedSkills={skillBases}
            occupationOptions={occupationOptions}
            suggestedSkills={occupationSkillOptions}
            skillsError={skillsError}
            onSkillSearchChange={setSkillQuery}
            onOccupationSearchChange={setOccupationQuery}
            onOccupationSearchSelect={handleSelectOccupation}
            onUpdateSkills={handleUpdateSkills}
            onCloseSkillsDialog={handleCloseSkillsDialog}
            className={className}
        />
    );
};

export default ConnectedSkillsCard;
